import React, { useEffect, useMemo, useState } from 'react';
import Container from '~/App/components/Container';
import Header from '../../../components/Header';
import { useNavigate } from 'react-router-dom';
import StickyHeader from '../../../components/StickyHeader';
import OrderHistoryItemRow from './OrderHistoryItemRow';
import { Empty, Pagination, PaginationProps } from 'antd';
import { OrderStatus } from '@restaurant/types';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { api, OrderListFilter } from '@restaurant/api';
import ErrorBoundary from '~/App/ErrorBoundary';
import FixedBottom from '~/App/components/FixedBottom';
import Loading from '~/App/components/Loading';
import { useErrorBoundary } from 'react-error-boundary';
import Filter from './components/Filter';

const todayStartTimestamp = moment().startOf('day').unix();
const todayEndTimestamp = moment().endOf('day').unix();

const PAGE_SIZE = 15;

const OrderHistoryItems = ({
  filter,
}: {
  filter: OrderListFilter | undefined;
}) => {
  const [page, setPage] = useState(1);
  const onChange: PaginationProps['onChange'] = (page) => {
    setPage(page);
  };
  const { showBoundary } = useErrorBoundary();

  const { data, isFetching, error } = useQuery({
    queryKey: ['fetchOrderList', filter, page],
    queryFn: () => {
      return api.fetchOrderList({
        page,
        size: PAGE_SIZE,
        filter: {
          statuses: [OrderStatus.Closed, OrderStatus.Refund, OrderStatus.Void],
          startDateTimeOrder: todayStartTimestamp,
          endDateTimeOrder: todayEndTimestamp,
          ...filter,
        },
      });
    },
  });
  const items = data?.data;

  useEffect(() => {
    if (error) {
      showBoundary(error);
    }
  }, [error]);

  const content = (() => {
    if (items?.length) {
      return items.map((item) => (
        <OrderHistoryItemRow order={item} key={item.orderId} />
      ));
    }
    if (!isFetching) {
      return (
        <div className="mt-[200px]">
          <Empty />
        </div>
      );
    }
  })();

  return (
    <>
      <div className="flex flex-col pb-[60px] relative flex-1">
        {isFetching ? (
          <Loading className="absolute inset-0 bg-white/50 flex-1" />
        ) : null}
        {content}
      </div>
      <FixedBottom>
        <div
          className="w-full h-[60px] bg-white flex justify-center items-center"
          style={{ borderTop: '1px solid #eee' }}
        >
          <Pagination
            current={page}
            onChange={onChange}
            total={data?.pagination.totalItems}
            pageSize={PAGE_SIZE}
          />
        </div>
      </FixedBottom>
    </>
  );
};

const timestampEndField = new Set(['endDateTimeOrder', 'endDateTimePayment']);

const OrderHistory = () => {
  const [filter, setFilter] = useState<OrderListFilter>();
  const navigate = useNavigate();

  const filterNumberIcon = useMemo(() => {
    if (!filter) return 0;
    return Object.keys(filter).filter((val) => {
      return !timestampEndField.has(val);
    }).length;
  }, [filter]);

  const filterKey = useMemo(() => {
    return JSON.stringify(filter);
  }, [filter]);

  return (
    <Container>
      <StickyHeader>
        <Header
          title="Histori Order"
          onBack={() => {
            navigate('/');
          }}
        />
        <div
          className="w-full flex justify-between px-4 py-2 bg-white"
          style={{ borderBottom: '1px solid #eee' }}
        >
          <Filter setValue={setFilter} numberIcon={filterNumberIcon} />
        </div>
      </StickyHeader>
      <ErrorBoundary>
        <OrderHistoryItems key={filterKey} filter={filter} />
      </ErrorBoundary>
    </Container>
  );
};

export default OrderHistory;
